import type { AuthSig } from "@lit-protocol/types";
import { base64 } from "@scure/base";
import { getPkpAddressFromSessionSig } from "./pkp";
import {
	type LIT_NETWORK,
	LIT_SESSIONSIG_AUTHORIZATION_SCHEMA_PREFIX,
	supportedNetworks,
	WRAPPED_KEY_SERVICE_URL_BY_LIT_NETWORK,
} from "./litConstants";
import { makeRequest } from "./makeRequest";

/**
 * The below methods are used to interact with the Lit Protocol wrapped key service.
 * They were extracted from the Lit Protocol wrapped key service source code.
 * This is due to the fact that when importing from that source code the entire library is imported,
 * which ends up bloating the compiled code by about ~2MB.
 */

function generateRequestId() {
	return crypto.randomUUID();
}

function composeAuthHeader(sessionSig: AuthSig) {
	// Convert string to Uint8Array using TextEncoder (UTF-8 encoding)
	const sessionSigUintArr = new TextEncoder().encode(
		JSON.stringify(sessionSig),
	);
	// Convert Uint8Array to base64 using @scure/base
	return `${LIT_SESSIONSIG_AUTHORIZATION_SCHEMA_PREFIX}${base64.encode(sessionSigUintArr)}`;
}

function isSupportedLitNetwork(litNetwork: LIT_NETWORK) {
	if (!supportedNetworks.includes(litNetwork)) {
		throw new Error(
			`Unsupported LIT_NETWORK! (${supportedNetworks.join("|")}) are supported.`,
		);
	}
}

function getServiceUrl({ litNetwork }: { litNetwork: LIT_NETWORK }) {
	isSupportedLitNetwork(litNetwork);
	return WRAPPED_KEY_SERVICE_URL_BY_LIT_NETWORK[litNetwork];
}

function getBaseRequestParams(requestParams: {
	sessionSig: AuthSig;
	method: string;
	litNetwork: LIT_NETWORK;
	requestId: string;
}) {
	const { sessionSig, method, litNetwork } = requestParams;
	return {
		baseUrl: getServiceUrl(requestParams),
		initParams: {
			method,
			headers: {
				"x-correlation-id": requestParams.requestId,
				"Content-Type": "application/json",
				"Lit-Network": litNetwork,
				Authorization: composeAuthHeader(sessionSig),
			},
		},
	};
}

interface FetchWrappedKeyParams {
	id: string;
	sessionSig: AuthSig;
	litNetwork: LIT_NETWORK;
}

interface FetchWrappedKeyResponse {
	ciphertext: string;
	dataToEncryptHash: string;
}

/**
 * Fetches a private key from the Lit Protocol wrapped key service.
 * The provided session signature is used to authenticate the request.
 *
 * @param {FetchWrappedKeyParams} params - The parameters for fetching a wrapped key.
 * @param {string} params.id - The ID of the wrapped key to fetch.
 * @param {AuthSig} params.sessionSig - The session signature to use for authentication.
 * @param {LIT_NETWORK} params.litNetwork - The network to use for the fetch.
 * @returns {Promise<FetchWrappedKeyResponse>} - The wrapped key data.
 */
export async function fetchWrappedKey({
	id,
	sessionSig,
	litNetwork,
}: FetchWrappedKeyParams): Promise<FetchWrappedKeyResponse> {
	const pkpAddress = getPkpAddressFromSessionSig(sessionSig);

	const requestId = generateRequestId();
	const { baseUrl, initParams } = getBaseRequestParams({
		litNetwork,
		sessionSig,
		method: "GET",
		requestId,
	});

	const requestURL = `${baseUrl}/${pkpAddress}/${id}`;
	const response = await makeRequest<FetchWrappedKeyResponse>(
		requestURL,
		initParams,
	);

	return response;
}

interface StoreWrappedKeyParams {
	sessionSig: AuthSig;
	litNetwork: LIT_NETWORK;
	storedKeyMetadata: {
		ciphertext: string;
		publicKey: string;
		keyType: string;
		dataToEncryptHash: string;
		memo: string;
	};
}

interface StoreWrappedKeyResponse {
	id: string;
	pkpAddress: string;
}

/**
 * Stores a private key in the Lit Protocol wrapped key service.
 * The provided session signature is used to authenticate the request.
 *
 * @param {StoreWrappedKeyParams} params - The parameters for storing a wrapped key.
 * @param {AuthSig} params.sessionSig - The session signature to use for authentication.
 * @param {LIT_NETWORK} params.litNetwork - The network to use for storage.
 * @param {Object} params.storedKeyMetadata - The key metadata to store.
 * @returns {Promise<StoreWrappedKeyResponse>} - The stored key ID and PKP address.
 */
export async function storeWrappedKey({
	sessionSig,
	litNetwork,
	storedKeyMetadata,
}: StoreWrappedKeyParams): Promise<StoreWrappedKeyResponse> {
	const pkpAddress = getPkpAddressFromSessionSig(sessionSig);

	const requestId = generateRequestId();
	const { baseUrl, initParams } = getBaseRequestParams({
		litNetwork,
		sessionSig,
		method: "POST",
		requestId,
	});

	const requestURL = baseUrl;
	const response = await makeRequest<StoreWrappedKeyResponse>(
		requestURL,
		{
			...initParams,
			body: JSON.stringify(storedKeyMetadata),
		},
	);

	return response;
}
